import { useState, useMemo } from "react";
import { useNavigate } from 'react-router';
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Building2, MapPin, Beaker, Filter, Wind, CircleDot, Layers,
  CheckCircle2, AlertCircle, ShieldAlert, ArrowRight, Database,
  FileText, Save, Factory, FlaskConical, Droplets, Gauge, TrendingUp,
  FileSignature,
} from "lucide-react";
import MylarProposalsTab from '@/components/MylarProposalsTab';
import PageHeader from '@/components/PageHeader';
import { saveLocalInfluentSample, saveLocalSite, saveLocalProcessTrain, type CustomerSite } from '@/lib/hybridStorage';
import {
  apiSaveCustomer, apiListCustomers, apiSaveInfluentSample, apiSaveProposal,
  apiSaveCustomerSite, apiListCustomerSites, apiSaveSiteEquipment,
} from '@/lib/phpApi';
import { inferUKRegulator, UK_REGULATOR_LABEL } from '@/lib/ukRegulator';
import { calcAerationColumn } from '@/pages/AerationColumnSpecPage';
import { calcGreensand } from '@/pages/GreensandSpecPage';
import { calcUF } from '@/pages/UFSpecPage';

/* ─── Mylar source-of-truth data ─── */

const MYLAR_CUSTOMER = {
  id: 'mylar-specialty-films',
  name: 'David Yates',
  company: 'Mylar Specialty Films',
  contactEmail: 'david.yates_contractor@mylar.com',
  phone: '+44 1387 258028',
  address: 'Dalbeattie Road, Drungans, Dumfries DG2 8LT, Scotland',
  notes: 'Contractor PM: David Yates (Homer Burgess Ltd). Boiler-feed pre-treatment for borehole supply. Plant runs 24/7.',
  createdAt: new Date().toISOString(),
  country: 'GB',
  industryType: 'Polyester film manufacture',
  standard: 'UK_EA',
  postcode: 'DG2 8LT',
  city: 'Dumfries',
  siteName: 'Drungans Boilerhouse',
  siteReference: 'MYLAR-DRUNGANS-2026',
  isDemo: false,
};

const MYLAR_SITE: Omit<CustomerSite, 'id' | 'createdAt' | 'updatedAt'> = {
  customerId: MYLAR_CUSTOMER.id,
  name: 'Drungans Boilerhouse — Borehole Pre-treatment',
  address: 'Dalbeattie Road, Drungans',
  postcode: 'DG2 8LT',
  country: 'GB',
  region: 'Scotland',
  stateName: 'Dumfries and Galloway',
  permitRef: 'N/A — closed-loop boiler feed',
  siteCategory: 'treatment_plant',
  standard: 'UK_EA',
  notes: 'Borehole 70 m³/h @ 9 barg pump, actual draw 24 m³/h. 200 m³ raw water tank → media filter (bypassed) → RO → 4 steam boilers. 24/7. Replace filter station before Dec 2026.',
};

// Veolia HW251217.24 borehole sample (28-Dec-2025) + PSD report
const MYLAR_SAMPLE_PARAMS: Record<string, number> = {
  // Flow
  flowRate: 24 * 24,         // m³/day (24 m³/h × 24h)
  // Chemistry
  ph: 7.8,
  alkalinity: 84,            // mg/L CaCO₃
  hardness: 205,             // mg/L CaCO₃
  tds: 280,
  conductivity: 530,         // µS/cm (estimated from TDS)
  // Iron / Mn
  iron: 0.47,
  fe: 0.47,
  manganese: 0.05,           // assumed pending test (Mylar's analyses didn't measure)
  mn: 0.05,
  // Solids / clarity
  tss: 1,                    // < 1 mg/L
  turbidity: 0.35,           // NTU (low — but PSD shows sub-µm load)
  sdi: 5.0,                  // estimated baseline for borehole with 1–3 µm particles
  // Anions (from Veolia)
  chloride: 60,              // ~84 mg/L as CaCO₃ → ~60 mg/L Cl
  sulphate: 17,
  nitrate: 7.6,
  silica: 8.3,
  free_co2: 4.4,
  // PSD summary (mean & dispersion)
  psd_mean_um: 1.25,
  psd_d10_volume_um: 1.24,
  psd_pct_sub_1um: 47,
  psd_pct_sub_4um: 100,
  // Temperature
  temp: 12,
  temperature: 12,
  // Boiler stage downstream — RO design basis (target)
  target_fe: 0.01,
  target_sdi: 3,
  ro_recovery: 80,
};

/* ─── Train simulator: pipes water through stages and tracks Fe / SDI / particles ─── */

interface StageResult {
  name: string;
  feIn: number; feOut: number;
  mnIn: number; mnOut: number;
  sdiIn: number; sdiOut: number;
  turbIn: number; turbOut: number;
  notes: string;
  meetsRoTarget: boolean; // Fe <= 0.05 mg/L for RO
}

interface TrainSimResult {
  stages: StageResult[];
  finalFe: number;
  finalMn: number;
  finalSdi: number;
  finalTurb: number;
  meetsRoTarget: boolean;
  capexEstimateGBP: number;
  opexAnnualGBP: number;
  notes: string[];
}

function simulateMylarTrain(trainKey: 'df_quoted' | 'df_revised' | 'budget' | 'recommended' | 'premium'): TrainSimResult {
  const baselineFe = 0.47;
  const baselineMn = 0.05;
  const baselineSdi = 5.0;
  const baselineTurb = 0.35;
  const flowMh = 24;

  const stages: StageResult[] = [];

  if (trainKey === 'df_quoted') {
    // DF as quoted — 2× DN1800 quartz only. No aeration, no catalytic media,
    // no coagulation, no polish. We push the actual borehole through plain quartz.
    // Physics: quartz alone cannot oxidise Fe²⁺ (no catalyst, no O₂ dose) and cannot
    // capture 1–3 µm particles (cut-off ~10–20 µm). Result: water passes effectively unchanged.
    const quartzStage: StageResult = {
      name: '2× DN1800 quartz (no pre-oxidation, no coagulation)',
      feIn: baselineFe, feOut: baselineFe * 0.97, // tiny mechanical removal of any pre-existing Fe³⁺ floc
      mnIn: baselineMn, mnOut: baselineMn * 0.99,
      sdiIn: baselineSdi, sdiOut: baselineSdi * 0.95, // negligible SDI improvement; no coagulation
      turbIn: baselineTurb, turbOut: baselineTurb * 0.90,
      notes: 'Quartz captures particles ≥10–20 µm. 100% of borehole particles are 1–3 µm — they pass straight through. No oxidation step → dissolved Fe²⁺ unaffected.',
      meetsRoTarget: false,
    };
    stages.push(quartzStage);

    const last = quartzStage;
    return {
      stages,
      finalFe: last.feOut, finalMn: last.mnOut, finalSdi: last.sdiOut, finalTurb: last.turbOut,
      meetsRoTarget: last.feOut <= 0.05 && last.sdiOut <= 3,
      capexEstimateGBP: 92_000, // €70k EXW → ~£62k + £20k Wildgoose install + £10k commissioning. Excludes backwash break tank, polish.
      opexAnnualGBP: 4_200,     // backwash water + ad-hoc media top-up
      notes: [
        '⚠ As-quoted: solves NEITHER of the two real problems. Fe stays at 0.45 mg/L (RO target 0.01). SDI stays around 4.8 (RO target ≤3).',
        '⚠ RO membrane fouling continues — the original reason for the project is unresolved.',
        '⚠ No backwash break tank in scope; towns mains @4 bar will not deliver the ~127 m³/h needed for DN1800 backwash.',
        '⚠ EXW only — UK install + commissioning + transport not in headline €70k figure. Realistic landed cost ≈ £92k once added.',
        '⚠ No PED/CE documentation in scope; needed for UK install.',
      ],
    };
  }

  if (trainKey === 'df_revised') {
    // DF revised priced offer (Re: Enquiry5, 28 May 2026, €180,000 DDU Scotland):
    //   DN1600 pressurised aeration + 2× DN1600 (anthracite + Multisand MnO2 catalytic)
    //   + 2× second-stage polishing filters. Painted carbon steel. SS316 pipework.
    //   Duty/standby backwash pumps, blower, compressor+receiver, E+H flow, Socla valves,
    //   control cabinet + automation, FAT + manuals + hygiene certs. DWI approved for UK.
    //   EXCLUDES: disinfection, water-quality analysers, design docs, install/commission/training.
    // Physics: aeration oxidises Fe(II)->Fe(III); Multisand MnO2 (natural ore, no KMnO4,
    //   regenerated by DO) captures Fe + Mn; granular polishing trims residual floc but,
    //   being granular (not absolute/UF), cannot reach SDI<3 against the 47%-sub-micron PSD load.

    // Stage 1 — DN1600 pressurised aeration column
    const aeration = calcAerationColumn({
      flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn, influentH2S: 0,
      ph: 7.8, alkalinity: 84, temperature: 12,
      columnDiameter: 1.6, columnHeight: 2.5, freeboard: 0.5,
      packingType: 'tellerette', packingSize: 50,
      airToWaterRatio: 8, airPressure: 0.4,
    });
    const aerStage: StageResult = {
      name: 'DN1600 pressurised aeration (Fe²⁺ → Fe³⁺ floc)',
      feIn: baselineFe, feOut: aeration.outletFe,
      mnIn: baselineMn, mnOut: aeration.outletMn,
      sdiIn: baselineSdi, sdiOut: baselineSdi + 0.2,
      turbIn: baselineTurb, turbOut: baselineTurb + 0.1,
      notes: `Fe oxidation ${aeration.feOxidationPct.toFixed(0)}%, DO ${aeration.doAchieved.toFixed(1)} mg/L. NOTE: vendor wants 4 bar inlet; site has ~2 barg — a booster is implied, and pressurised aeration at 4 bar lowers O₂-transfer efficiency vs an atmospheric tower.`,
      meetsRoTarget: false,
    };
    stages.push(aerStage);

    // Stage 2 — 2× DN1600 anthracite + Multisand MnO2 catalytic (natural ore, no KMnO4)
    const catalytic = calcGreensand({
      flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn,
      ph: 7.8, doIn: aeration.doAchieved, temperature: 12,
      vesselDiameter: 1.6, bedDepth: 0.9, freeboard: 0.5,
      numVessels: 2, arrangement: 'duplex_parallel',
      media: 'pyrolusite', kmno4Dose: 0,
      serviceVelocity: 8, backwashRate: 30,
    });
    const catStage: StageResult = {
      name: '2× DN1600 anthracite + Multisand MnO₂ catalytic (no KMnO₄)',
      feIn: aerStage.feOut, feOut: catalytic.outletFe,
      mnIn: aerStage.mnOut, mnOut: catalytic.outletMn,
      sdiIn: aerStage.sdiOut, sdiOut: 3.6,  // catalytic bed traps floc but coarse cut-off; sub-µm load persists
      turbIn: aerStage.turbOut, turbOut: 0.12,
      notes: `Fe ${catalytic.feRemovalPct.toFixed(0)}%, Mn ${catalytic.mnRemovalPct.toFixed(0)}%. Natural MnO₂ ore regenerated by dissolved O₂ — no permanganate, no pink-water risk. Mn sized on assumption (not lab-confirmed).`,
      meetsRoTarget: catalytic.outletFe <= 0.05,
    };
    stages.push(catStage);

    // Stage 3 — 2× second-stage polishing filters (granular, NOT absolute/UF)
    const polishStage: StageResult = {
      name: '2× second-stage polishing filters (granular)',
      feIn: catStage.feOut, feOut: catStage.feOut * 0.85,
      mnIn: catStage.mnOut, mnOut: catStage.mnOut * 0.95,
      sdiIn: catStage.sdiOut, sdiOut: 3.3,  // granular polish: trims SDI but cannot reach <3 (no UF, sub-µm load)
      turbIn: catStage.turbOut, turbOut: 0.08,
      notes: 'Granular polishing trims residual floc. Being granular (10–20 µm cut-off) it cannot reach SDI<3 against the 47%-sub-µm PSD — an absolute cartridge or UF barrier would be required for a guaranteed RO-feed SDI.',
      meetsRoTarget: false,
    };
    stages.push(polishStage);

    const last = polishStage;
    return {
      stages,
      finalFe: last.feOut, finalMn: last.mnOut, finalSdi: last.sdiOut, finalTurb: last.turbOut,
      meetsRoTarget: last.feOut <= 0.05 && last.sdiOut <= 3,
      capexEstimateGBP: 153_000, // €180k DDU ≈ £153k delivered (NOT installed; excludes install/commission/design/disinfection/analysers)
      opexAnnualGBP: 5_200,      // backwash water + power + periodic media top-up; no KMnO4 consumable
      notes: [
        '✓ Solves the Fe problem: aeration + Multisand MnO₂ drops Fe to ~0.01 mg/L and removes Mn — no KMnO₄ consumable.',
        '✓ Complete equipment package: duty/standby backwash pumps, blower, compressor+receiver, SS316 pipe, E+H flow, automation, FAT, manuals, hygiene certs. DDU Scotland.',
        '✓ DWI-approved filters for the UK market.',
        '⚠ SDI to RO NOT guaranteed: polishing is granular, not UF/absolute — final SDI ≈ 3.3 vs RO target ≤3 against a 47%-sub-µm load.',
        '⚠ Painted carbon-steel vessels in aerated/oxidising Fe-Mn duty — long-term corrosion risk; push for 304L or written lining spec.',
        '⚠ 4 bar inlet requested vs ~2 barg available on site — booster pump decision needed (not clearly in scope).',
        '⚠ EXCLUDES disinfection, online analysers, design documentation, install, commissioning, training — real cost beyond the €180k headline.',
        '€180,000 DDU ≈ £153k delivered. Once install/commissioning/design are added it approaches R&B Option B (£142k installed) — but Option B guarantees SDI and is 304L.',
      ],
    };
  }

  if (trainKey === 'budget') {
    // Train A — PACl coagulation + multimedia (no Fe oxidation, relies on multimedia + chem)
    // PACl + mixer
    const paclStage: StageResult = {
      name: 'PACl dosing + static mixer',
      feIn: baselineFe, feOut: baselineFe, // PACl alone doesn't reduce dissolved Fe
      mnIn: baselineMn, mnOut: baselineMn,
      sdiIn: baselineSdi, sdiOut: baselineSdi,
      turbIn: baselineTurb, turbOut: baselineTurb + 0.2, // flocs added
      notes: 'PACl 1.5 mg/L Al, flocs 1–3 µm particles into 10–20 µm capturable size',
      meetsRoTarget: false,
    };
    stages.push(paclStage);

    // Multimedia (DN1400) — now particles are 10–20 µm → catchable
    // Fe removal modest (PACl-assisted only); Mn essentially unchanged
    const mmStage: StageResult = {
      name: 'Multimedia DN1400 (anthracite+sand+garnet)',
      feIn: paclStage.feOut, feOut: paclStage.feOut * 0.35,  // 65% removal of co-precipitated Fe with PACl
      mnIn: paclStage.mnOut, mnOut: paclStage.mnOut * 0.95,  // negligible
      sdiIn: paclStage.sdiOut, sdiOut: 3.5,                  // good but not RO-grade
      turbIn: paclStage.turbOut, turbOut: 0.15,
      notes: 'PACl-assisted multimedia bed captures coagulated particles. Bare media would pass them.',
      meetsRoTarget: false,
    };
    stages.push(mmStage);

    // 5 µm cartridge polish
    const polishStage: StageResult = {
      name: '5 µm cartridge polish',
      feIn: mmStage.feOut, feOut: mmStage.feOut * 0.85,
      mnIn: mmStage.mnOut, mnOut: mmStage.mnOut * 0.98,
      sdiIn: mmStage.sdiOut, sdiOut: 3.2,
      turbIn: mmStage.turbOut, turbOut: 0.10,
      notes: 'Catches breakthrough flocs',
      meetsRoTarget: false,
    };
    stages.push(polishStage);

    const last = polishStage;
    return {
      stages,
      finalFe: last.feOut, finalMn: last.mnOut, finalSdi: last.sdiOut, finalTurb: last.turbOut,
      meetsRoTarget: last.feOut <= 0.05 && last.sdiOut <= 3,
      capexEstimateGBP: 95_000,
      opexAnnualGBP: 8_500, // PACl consumption + cartridges + backwash water
      notes: [
        'Lowest capex but only solves particles, not dissolved Fe — Fe sneaks through at ~0.14 mg/L vs RO target 0.01.',
        'RO membranes will continue to take some iron load — this option does NOT fully solve the original RO-life problem.',
        'PACl chemistry needs operator skill — overdose creates sludge / under-dose lets particles pass.',
      ],
    };
  }

  if (trainKey === 'recommended') {
    // Train B — Aeration + Greensand Plus + 5 µm polish
    const aeration = calcAerationColumn({
      flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn, influentH2S: 0,
      ph: 7.8, alkalinity: 84, temperature: 12,
      columnDiameter: 0.9, columnHeight: 2.5, freeboard: 0.5,
      packingType: 'tellerette', packingSize: 50,
      airToWaterRatio: 8, airPressure: 0.4,
    });
    const aerStage: StageResult = {
      name: 'Aeration column (Fe²⁺ → Fe³⁺ floc)',
      feIn: baselineFe, feOut: aeration.outletFe,
      mnIn: baselineMn, mnOut: aeration.outletMn,
      sdiIn: baselineSdi, sdiOut: baselineSdi + 0.2,
      turbIn: baselineTurb, turbOut: baselineTurb + 0.1,
      notes: `Fe oxidation ${aeration.feOxidationPct.toFixed(0)}%, DO ${aeration.doAchieved.toFixed(1)} mg/L. Fe now in particulate form, ready for catalytic media.`,
      meetsRoTarget: false,
    };
    stages.push(aerStage);

    // Greensand Plus
    const greensand = calcGreensand({
      flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn,  // catalytic media handles total Fe load
      ph: 7.8, doIn: aeration.doAchieved, temperature: 12,
      vesselDiameter: 1.6, bedDepth: 0.9, freeboard: 0.5,
      numVessels: 2, arrangement: 'duplex_parallel',
      media: 'greensand_plus', kmno4Dose: 1.5,
      serviceVelocity: 8, backwashRate: 30,
    });
    const gsStage: StageResult = {
      name: 'Greensand Plus DN1600 duplex (KMnO₄ regen)',
      feIn: aerStage.feOut, feOut: greensand.outletFe,
      mnIn: aerStage.mnOut, mnOut: greensand.outletMn,
      sdiIn: aerStage.sdiOut, sdiOut: 3.2,
      turbIn: aerStage.turbOut, turbOut: 0.10,
      notes: `Fe ${greensand.feRemovalPct.toFixed(0)}%, Mn ${greensand.mnRemovalPct.toFixed(0)}%. KMnO₄ ${greensand.kmno4ConsumptionKgD.toFixed(2)} kg/d`,
      meetsRoTarget: greensand.outletFe <= 0.05,
    };
    stages.push(gsStage);

    // 5 µm cartridge polish
    const polishStage: StageResult = {
      name: '5 µm cartridge polish (SDI protection)',
      feIn: gsStage.feOut, feOut: gsStage.feOut * 0.9,
      mnIn: gsStage.mnOut, mnOut: gsStage.mnOut * 0.95,
      sdiIn: gsStage.sdiOut, sdiOut: 2.8,
      turbIn: gsStage.turbOut, turbOut: 0.06,
      notes: 'Catches any floc breakthrough, protects RO from short-circuit events.',
      meetsRoTarget: true,
    };
    stages.push(polishStage);

    const last = polishStage;
    return {
      stages,
      finalFe: last.feOut, finalMn: last.mnOut, finalSdi: last.sdiOut, finalTurb: last.turbOut,
      meetsRoTarget: last.feOut <= 0.05 && last.sdiOut <= 3,
      capexEstimateGBP: 142_000,
      opexAnnualGBP: 6_500, // KMnO₄ + cartridges + backwash water + electricity
      notes: [
        'Solves the actual problem: drops Fe from 0.47 → ~0.005 mg/L (RO target 0.01 met).',
        'Duplex parallel = no boiler interruption during backwash.',
        'KMnO₄ dosing rig needs occasional operator attention (refill every ~2 weeks).',
        'Carryover guard prevents pink-water reaching RO membranes.',
      ],
    };
  }

  // Premium — Aeration + Greensand Plus + UF + polish
  const aeration = calcAerationColumn({
    flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn, influentH2S: 0,
    ph: 7.8, alkalinity: 84, temperature: 12,
    columnDiameter: 0.9, columnHeight: 2.5, freeboard: 0.5,
    packingType: 'tellerette', packingSize: 50,
    airToWaterRatio: 8, airPressure: 0.4,
  });
  const aerStage: StageResult = {
    name: 'Aeration column (Fe²⁺ → Fe³⁺ floc)',
    feIn: baselineFe, feOut: aeration.outletFe,
    mnIn: baselineMn, mnOut: aeration.outletMn,
    sdiIn: baselineSdi, sdiOut: baselineSdi + 0.2,
    turbIn: baselineTurb, turbOut: baselineTurb + 0.1,
    notes: `Fe oxidation ${aeration.feOxidationPct.toFixed(0)}%, DO ${aeration.doAchieved.toFixed(1)} mg/L.`,
    meetsRoTarget: false,
  };
  stages.push(aerStage);

  const greensand = calcGreensand({
    flowRate: flowMh, influentFe: baselineFe, influentMn: baselineMn,
    ph: 7.8, doIn: aeration.doAchieved, temperature: 12,
    vesselDiameter: 1.4, bedDepth: 0.9, freeboard: 0.5,
    numVessels: 2, arrangement: 'duplex_parallel',
    media: 'greensand_plus', kmno4Dose: 1.5,
    serviceVelocity: 8, backwashRate: 30,
  });
  const gsStage: StageResult = {
    name: 'Greensand Plus DN1400 duplex',
    feIn: aerStage.feOut, feOut: greensand.outletFe,
    mnIn: aerStage.mnOut, mnOut: greensand.outletMn,
    sdiIn: aerStage.sdiOut, sdiOut: 3.0,
    turbIn: aerStage.turbOut, turbOut: 0.10,
    notes: `Fe ${greensand.feRemovalPct.toFixed(0)}%, Mn ${greensand.mnRemovalPct.toFixed(0)}%`,
    meetsRoTarget: greensand.outletFe <= 0.05,
  };
  stages.push(gsStage);

  // UF skid
  const uf = calcUF({
    flowRate: flowMh, influentTurbidity: gsStage.turbOut, influentTSS: 1, influentSDI: gsStage.sdiOut,
    influentFe: gsStage.feOut, temperature: 12,
    configuration: 'outside_in_pressurised', fibre: 'pvdf', poreSize: 0.03,
    fibreOD: 1.3, fibreID: 0.7, fibresPerModule: 9000, moduleArea: 50,
    numModules: 4,
    flux: 60, recovery: 92, backwashInterval: 30, backwashDuration: 60,
    cebInterval: 12, cipInterval: 30,
  });
  const ufStage: StageResult = {
    name: 'UF skid PVDF 0.03 µm (4× modules)',
    feIn: gsStage.feOut, feOut: gsStage.feOut * 0.05, // UF rejects iron floc completely
    mnIn: gsStage.mnOut, mnOut: gsStage.mnOut * 0.10,
    sdiIn: gsStage.sdiOut, sdiOut: uf.outletSDI,
    turbIn: gsStage.turbOut, turbOut: uf.outletTurbidity,
    notes: `Sub-µm physical barrier. Rejection ${uf.particleRejectionPct.toFixed(2)}%, SDI guarantee <3 ✓`,
    meetsRoTarget: true,
  };
  stages.push(ufStage);

  const polishStage: StageResult = {
    name: '5 µm cartridge polish (belt + braces)',
    feIn: ufStage.feOut, feOut: ufStage.feOut,
    mnIn: ufStage.mnOut, mnOut: ufStage.mnOut,
    sdiIn: ufStage.sdiOut, sdiOut: ufStage.sdiOut,
    turbIn: ufStage.turbOut, turbOut: ufStage.turbOut,
    notes: 'Last-line cartridge; mostly serves to alarm on UF integrity failure.',
    meetsRoTarget: true,
  };
  stages.push(polishStage);

  const last = polishStage;
  return {
    stages,
    finalFe: last.feOut, finalMn: last.mnOut, finalSdi: last.sdiOut, finalTurb: last.turbOut,
    meetsRoTarget: last.feOut <= 0.05 && last.sdiOut <= 3,
    capexEstimateGBP: 215_000,
    opexAnnualGBP: 11_000, // KMnO₄ + UF chemicals + cartridges
    notes: [
      'Belt-and-braces: UF physically guarantees SDI <3 regardless of catalytic media drift.',
      'Stainless 316L vessels — 15+ year asset life with minimal maintenance.',
      'Includes BMS integration and removal of existing stations in capex.',
      'Best long-term investment — protects RO under all operating scenarios.',
    ],
  };
}

/* ─── Page ─── */

const TRAIN_DEFS = [
  {
    key: 'df_quoted' as const,
    name: 'DF Quoted — As-is',
    subtitle: '2× DN1800 quartz only (Dynamik Filtr €70k EXW)',
    description: 'The Dynamik Filtr offer from 26 May 2026, evaluated against the actual water data. Priced before chemistry / PSD were shared.',
    color: '#f43f5e',
    accent: 'rose',
    equipmentRoutes: [
      { name: 'Multimedia ref', route: '/multimedia-spec', icon: Layers },
    ],
  },
  {
    key: 'df_revised' as const,
    name: 'DF Revised — €180k Priced',
    subtitle: 'Aeration + Multisand MnO₂ + polishing (Dynamik Filtr €180k DDU)',
    description: 'Dynamik Filtr firm priced offer (Re: Enquiry5, 28 May 2026). Complete equipment package, DWI-approved, DDU Scotland. Painted CS; no UF/SDI guarantee; no disinfection/analysers/design/install.',
    color: '#fb923c',
    accent: 'orange',
    equipmentRoutes: [
      { name: 'DN1600 Aeration', route: '/aeration-column-spec', icon: Wind },
      { name: 'Multisand MnO₂ DN1600', route: '/greensand-spec', icon: Filter },
      { name: 'Polishing filters', route: '/multimedia-spec', icon: Filter },
    ],
  },
  {
    key: 'budget' as const,
    name: 'Option A — Budget',
    subtitle: 'PACl + multimedia + cartridge polish',
    description: '2× DN1400 multimedia with PACl coagulation. Lowest capex; partially solves the problem.',
    color: '#fbbf24',
    accent: 'amber',
    equipmentRoutes: [
      { name: 'PACl Dosing', route: '/chemical-dosing', icon: FlaskConical },
      { name: 'Multimedia DN1400', route: '/multimedia-spec', icon: Layers },
      { name: '5 µm Polish', route: '/multimedia-spec', icon: Filter },
    ],
  },
  {
    key: 'recommended' as const,
    name: 'Option B — Recommended',
    subtitle: 'Aeration + Greensand Plus + cartridge polish',
    description: '2× DN1600 Greensand Plus duplex with KMnO₄ regen. Solves Fe AND particulates within RO design basis.',
    color: '#10b981',
    accent: 'emerald',
    equipmentRoutes: [
      { name: 'Aeration Column', route: '/aeration-column-spec', icon: Wind },
      { name: 'Greensand Plus DN1600', route: '/greensand-spec', icon: Filter },
      { name: '5 µm Polish', route: '/multimedia-spec', icon: Filter },
    ],
  },
  {
    key: 'premium' as const,
    name: 'Option C — Premium',
    subtitle: 'Aeration + Greensand + UF + cartridge polish',
    description: 'Catalytic + UF guarantee SDI <3. Future-proof, BMS-integrated, includes existing removal.',
    color: '#a855f7',
    accent: 'purple',
    equipmentRoutes: [
      { name: 'Aeration Column', route: '/aeration-column-spec', icon: Wind },
      { name: 'Greensand Plus DN1400', route: '/greensand-spec', icon: Filter },
      { name: 'UF skid PVDF', route: '/uf-spec', icon: CircleDot },
      { name: '5 µm Polish', route: '/multimedia-spec', icon: Filter },
    ],
  },
];

/** Save a process train to the DB. No phpApi helper exists for trains yet, so post directly. */
async function apiSaveProcessTrain(payload: Record<string, unknown>): Promise<{ success: boolean; id: number }> {
  const token = (() => {
    try { return localStorage.getItem('rb_auth_token') || ''; } catch { return ''; }
  })();
  const body = token ? { ...payload, __token: token } : payload;
  const url = token
    ? `/api/process-trains/save.php?_token=${encodeURIComponent(token)}`
    : '/api/process-trains/save.php';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to save process train');
  return res.json();
}

export default function MylarBoreholeAssessmentPage() {
  const navigate = useNavigate();
  const [seeded, setSeeded] = useState(false);
  const [dbState, setDbState] = useState<'idle' | 'pushing' | 'done' | 'error'>('idle');
  const [dbLog, setDbLog] = useState<string[]>([]);
  const [activeTrain, setActiveTrain] = useState<'df_quoted' | 'df_revised' | 'budget' | 'recommended' | 'premium'>('df_revised');
  // Map of trainKey → saved DB process-train id, so the cards can deep-link to the
  // actual generated train (created under Customers → Sites → Trains). Persisted so
  // the links survive a page reload.
  const [savedTrainIds, setSavedTrainIds] = useState<Record<string, number>>(() => {
    try { return JSON.parse(localStorage.getItem('rb_mylar_train_ids_v1') || '{}'); }
    catch { return {}; }
  });
  const [activeTab, setActiveTab] = useState<'assessment' | 'automation' | 'pricing' | 'proposals'>('assessment');

  const trainResult = useMemo(() => simulateMylarTrain(activeTrain), [activeTrain]);

  const allTrainResults = useMemo(() => ({
    df_quoted: simulateMylarTrain('df_quoted'),
    df_revised: simulateMylarTrain('df_revised'),
    budget: simulateMylarTrain('budget'),
    recommended: simulateMylarTrain('recommended'),
    premium: simulateMylarTrain('premium'),
  }), []);

  /* Seed: Customer + Site + Sample + 3 Trains + Proposal into localStorage */
  const handleSeed = () => {
    try {
      // 1. Customer
      const customersRaw = localStorage.getItem('rb_customers');
      const customers = customersRaw ? JSON.parse(customersRaw) : [];
      const existing = customers.find((c: any) => c.id === MYLAR_CUSTOMER.id || c.company === MYLAR_CUSTOMER.company);
      if (!existing) {
        customers.unshift(MYLAR_CUSTOMER);
        localStorage.setItem('rb_customers', JSON.stringify(customers));
      }

      // 2. Site
      const site = saveLocalSite(MYLAR_SITE);

      // 3. Sample (borehole)
      const sample = saveLocalInfluentSample({
        customerId: 0,  // will display under name; numeric stub fine for local
        siteId: site.id,
        sampleName: 'Boilerhouse Borehole — Veolia HW251217.24 (28-Dec-2025)',
        reportNumber: 'HW251217.24.082',
        params: MYLAR_SAMPLE_PARAMS,
        standard: 'UK_EA',
        sampleType: 'influent',
        notes: 'Veolia chemistry + PSD report. 100% of particles <4 µm, mean 1.25 µm, D10(volume) 1.24 µm, 47% sub-µm. Iron 0.47 mg/L vs RO design basis of 0.01 mg/L — primary design driver.',
      });

      // 4. Process trains × 3
      const trainKeys: Array<'df_quoted' | 'df_revised' | 'budget' | 'recommended' | 'premium'> = ['df_quoted', 'df_revised', 'budget', 'recommended', 'premium'];
      const trainIds: string[] = [];
      trainKeys.forEach((tk) => {
        const def = TRAIN_DEFS.find(t => t.key === tk)!;
        const sim = simulateMylarTrain(tk);
        const train = saveLocalProcessTrain({
          userId: 0,
          name: `Mylar Drungans — ${def.name}`,
          description: def.description,
          sampleId: sample.id,
          customerId: 0,
          equipmentList: sim.stages.map((s, i) => ({
            order: i + 1,
            name: s.name,
            feIn: s.feIn, feOut: s.feOut, sdiIn: s.sdiIn, sdiOut: s.sdiOut,
          })),
          status: 'assessed',
          assessmentResult: {
            finalFe: sim.finalFe,
            finalMn: sim.finalMn,
            finalSdi: sim.finalSdi,
            finalTurb: sim.finalTurb,
            meetsRoTarget: sim.meetsRoTarget,
            capexEstimateGBP: sim.capexEstimateGBP,
            opexAnnualGBP: sim.opexAnnualGBP,
            notes: sim.notes,
          },
        });
        trainIds.push(String(train.id));
      });

      // 5. Proposal — UK format (UK-PROP/2026/NNNNN/R0)
      const seqMapRaw = localStorage.getItem('rb_proposal_seq_v1');
      const seqMap = seqMapRaw ? JSON.parse(seqMapRaw) : {};
      const year = new Date().getFullYear();
      const nextSeq = (seqMap[String(year)] || 0) + 1;
      seqMap[String(year)] = nextSeq;
      localStorage.setItem('rb_proposal_seq_v1', JSON.stringify(seqMap));
      const seqStr = String(nextSeq).padStart(5, '0');
      const baseNumber = `UK-PROP/${year}/${seqStr}`;
      const proposalNumber = `${baseNumber}/R0`;
      const proposal = {
        id: `local_${Date.now()}`,
        proposalNumber, baseNumber, revisionNumber: 0,
        title: 'Mylar Specialty Films — Drungans borehole pre-treatment replacement',
        description: '3 options for replacing bypassed media filter station upstream of existing RO. Addresses 0.47 mg/L dissolved iron and 1–3 µm particulate load not currently captured by existing equipment.',
        customerId: MYLAR_CUSTOMER.id,
        customerName: MYLAR_CUSTOMER.company,
        status: 'draft',
        isLocalOnly: true,
        savedToDb: false,
        syncStatus: 'pending',
        countryCode: 'GB',
        standard: 'UKWIR/WIMES',
        processTrainIds: trainIds,
        sampleIds: [String(sample.id)],
        notes: 'Quote sources: Dynamik Filtr (PL, EXW Częstochowa) + Wildgoose Environmental (UK install). David Yates is contractor PM at Homer Burgess Ltd acting for Mylar. Install required before Dec 2026.',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const proposalsRaw = localStorage.getItem('rb_proposals_v2');
      const proposals = proposalsRaw ? JSON.parse(proposalsRaw) : [];
      proposals.unshift(proposal);
      localStorage.setItem('rb_proposals_v2', JSON.stringify(proposals));

      setSeeded(true);
      toast.success(`Mylar records seeded locally — Proposal ${proposalNumber}`);
    } catch (e) {
      console.error(e);
      toast.error('Seed failed — check console');
    }
  };

  /* Push to DB — Customer + Sample + 4 Trains + Proposal via PHP API */
  const handlePushToDb = async () => {
    setDbState('pushing');
    const log: string[] = [];
    try {
      // 1. Customer — lookup existing by company first, only create if missing
      log.push('Looking up customer Mylar Specialty Films in DB…');
      setDbLog([...log]);
      let customerId = 0;
      try {
        const existing = await apiListCustomers();
        const match = (existing as any[]).find((c: any) =>
          (c.company || '').toLowerCase().includes('mylar') ||
          (c.name || '').toLowerCase().includes('mylar')
        );
        if (match?.id) {
          customerId = Number(match.id);
          log.push(`✓ Customer already exists — reusing id=${customerId}`);
        }
      } catch { /* ignore — fall through to create */ }
      if (!customerId) {
        log.push('Creating new customer…');
        setDbLog([...log]);
        const sepaStd = inferUKRegulator(MYLAR_SITE.postcode);
        log.push(`✓ Regulator auto-detected from postcode ${MYLAR_SITE.postcode} → ${sepaStd} (${UK_REGULATOR_LABEL[sepaStd]})`);
        const custRes = await apiSaveCustomer({
          name: MYLAR_CUSTOMER.name,
          company: MYLAR_CUSTOMER.company,
          email: MYLAR_CUSTOMER.contactEmail,
          phone: MYLAR_CUSTOMER.phone,
          address: MYLAR_CUSTOMER.address,
          notes: MYLAR_CUSTOMER.notes,
          standard: sepaStd,
        });
        customerId = Number(custRes.id);
        if (!customerId) throw new Error('Customer save returned no id');
        log.push(`✓ Customer created id=${customerId}`);
      }
      setDbLog([...log]);

      // 2. Site — lookup existing by name+customer first, create if missing
      log.push('Looking up site Drungans Boilerhouse in DB…');
      setDbLog([...log]);
      let siteId = 0;
      try {
        const existingSites = await apiListCustomerSites(customerId);
        const matchSite = (existingSites as any[]).find((s: any) =>
          (s.name || '').toLowerCase().includes('drungans')
        );
        if (matchSite?.id) {
          siteId = Number(matchSite.id);
          log.push(`✓ Site already exists — reusing id=${siteId}`);
        }
      } catch { /* fall through */ }
      if (!siteId) {
        log.push('Creating new site…');
        setDbLog([...log]);
        const siteStd = inferUKRegulator(MYLAR_SITE.postcode);
        const siteRes = await apiSaveCustomerSite({
          customerId,
          name: MYLAR_SITE.name,
          address: MYLAR_SITE.address,
          postcode: MYLAR_SITE.postcode,
          region: MYLAR_SITE.region,
          country: MYLAR_SITE.country,
          permitRef: MYLAR_SITE.permitRef,
          standard: siteStd,
          notes: MYLAR_SITE.notes,
        });
        siteId = Number(siteRes.id);
        if (!siteId) throw new Error('Site save returned no id');
        log.push(`✓ Site created id=${siteId}`);
      }
      setDbLog([...log]);

      // 3. Sample (borehole)
      log.push('Saving borehole sample (Veolia HW251217.24)…');
      setDbLog([...log]);
      const sampleRes = await apiSaveInfluentSample({
        customerId,
        sampleName: 'Boilerhouse Borehole — Veolia HW251217.24 (28-Dec-2025)',
        reportNumber: 'HW251217.24.082',
        params: MYLAR_SAMPLE_PARAMS,
        standard: 'UK_EA',
        sampleType: 'influent',
        notes: `Site: ${MYLAR_SITE.name}, ${MYLAR_SITE.address}, ${MYLAR_SITE.postcode}.\nVeolia chemistry + PSD report. 100% of particles <4 µm, mean 1.25 µm, D10(volume) 1.24 µm, 47% sub-µm. Iron 0.47 mg/L vs RO design basis of 0.01 mg/L — primary design driver.`,
      });
      const sampleId = Number(sampleRes.id ?? sampleRes.sampleId ?? 0);
      log.push(`✓ Sample id=${sampleId}`);
      setDbLog([...log]);

      // 4. Process trains × 4 + per-train site equipment assignments
      const trainKeys: Array<'df_quoted' | 'df_revised' | 'budget' | 'recommended' | 'premium'> = ['df_quoted', 'df_revised', 'budget', 'recommended', 'premium'];
      const dbTrainIds: number[] = [];
      const trainIdMap: Record<string, number> = {};
      for (const tk of trainKeys) {
        const def = TRAIN_DEFS.find(t => t.key === tk)!;
        const sim = simulateMylarTrain(tk);
        log.push(`Saving train: ${def.name}…`);
        setDbLog([...log]);
        const equipmentList = sim.stages.map((s, i) => ({
          order: i + 1,
          name: s.name,
          feIn: s.feIn, feOut: s.feOut,
          mnIn: s.mnIn, mnOut: s.mnOut,
          sdiIn: s.sdiIn, sdiOut: s.sdiOut,
          turbIn: s.turbIn, turbOut: s.turbOut,
          notes: s.notes,
          meetsRoTarget: s.meetsRoTarget,
        }));
        const trainRes = await apiSaveProcessTrain({
          name: `Mylar Drungans — ${def.name}`,
          description: def.description,
          sampleId,
          customerId,
          siteId,
          equipmentList,
          status: 'assessed',
          assessmentResult: {
            finalFe: sim.finalFe,
            finalMn: sim.finalMn,
            finalSdi: sim.finalSdi,
            finalTurb: sim.finalTurb,
            meetsRoTarget: sim.meetsRoTarget,
            capexEstimateGBP: sim.capexEstimateGBP,
            opexAnnualGBP: sim.opexAnnualGBP,
            notes: sim.notes,
            simulatedFromSampleId: sampleId,
            customerId,
            siteId,
            trainKey: tk,
          },
        });
        const tid = Number(trainRes.id);
        dbTrainIds.push(tid);
        if (tid) trainIdMap[tk] = tid;
        log.push(`  ✓ Train id=${tid}`);
        setDbLog([...log]);

        // 4b. Site equipment assignment for this train
        try {
          const eqRes = await apiSaveSiteEquipment({
            siteId,
            customerId,
            trainKey: tk,
            trainLabel: def.name,
            processTrainId: tid,
            equipment: sim.stages.map((s, i) => ({
              equipmentKey: tk === 'df_quoted' ? 'multimedia_quartz_only'
                          : i === 0 && tk === 'budget' ? 'pacl_dosing'
                          : i === 0 && (tk === 'recommended' || tk === 'premium') ? 'aeration_column'
                          : s.name.toLowerCase().includes('greensand') ? 'greensand'
                          : s.name.toLowerCase().includes('uf') ? 'uf'
                          : s.name.toLowerCase().includes('multimedia') ? 'multimedia'
                          : s.name.toLowerCase().includes('polish') ? 'cartridge_polish_5um'
                          : 'item',
              equipmentName: s.name,
              order: i + 1,
              configJson: {
                feIn: s.feIn, mnIn: s.mnIn, sdiIn: s.sdiIn, turbIn: s.turbIn,
              },
              outcomeJson: {
                feOut: s.feOut, mnOut: s.mnOut, sdiOut: s.sdiOut, turbOut: s.turbOut,
                meetsRoTarget: s.meetsRoTarget,
              },
              notes: s.notes,
              enabled: true,
            })),
          });
          log.push(`    ✓ Site equipment assigned (${eqRes.inserted} items)`);
        } catch (eqErr: any) {
          log.push(`    ✗ Site equipment failed: ${eqErr?.message ?? eqErr}`);
        }
        setDbLog([...log]);
      }

      // Persist the trainKey → saved id map so the option cards can deep-link to the
      // actual generated trains (Customers → Sites → Trains).
      try { localStorage.setItem('rb_mylar_train_ids_v1', JSON.stringify(trainIdMap)); } catch { /* */ }
      setSavedTrainIds(trainIdMap);

      // 4. Proposal — UK format (UK-PROP/YYYY/NNNNN/R0)
      const year = new Date().getFullYear();
      const seqMapRaw = localStorage.getItem('rb_proposal_seq_v1');
      const seqMap = seqMapRaw ? JSON.parse(seqMapRaw) : {};
      const nextSeq = (seqMap[String(year)] || 0) + 1;
      seqMap[String(year)] = nextSeq;
      localStorage.setItem('rb_proposal_seq_v1', JSON.stringify(seqMap));
      const seqStr = String(nextSeq).padStart(5, '0');
      const baseNumber = `UK-PROP/${year}/${seqStr}`;
      const proposalNumber = `${baseNumber}/R0`;
      log.push(`Saving proposal ${proposalNumber}…`);
      setDbLog([...log]);
      const propRes = await apiSaveProposal({
        proposalNumber,
        baseNumber,
        revisionNumber: 0,
        title: 'Mylar Specialty Films — Drungans borehole pre-treatment replacement',
        description: '4 options compared (DF as-quoted vs 3 engineered alternatives) for replacing bypassed media filter station upstream of existing RO. Primary design driver: dropping Fe from 0.47 mg/L to ≤0.01 mg/L and SDI to ≤3.',
        customerId,
        customerName: MYLAR_CUSTOMER.company,
        status: 'draft',
        countryCode: 'GB',
        standard: 'UKWIR/WIMES',
        processTrainIds: dbTrainIds.map(String),
        sampleIds: [String(sampleId)],
        notes: `Quote sources: Dynamik Filtr (PL, EXW Częstochowa €70k as-quoted) + Wildgoose Environmental (UK install). Contractor PM: David Yates (Homer Burgess Ltd). Install required before Dec 2026.\n\nSite: ${MYLAR_SITE.name}\nAddress: ${MYLAR_SITE.address}, ${MYLAR_SITE.postcode}\nRegion: ${MYLAR_SITE.region}\n\nEquipment assignments (per train) are stored in process_trains.equipmentList JSON.`,
      });
      log.push(`✓ Proposal id=${propRes.id ?? '?'} number=${proposalNumber}`);
      setDbLog([...log]);

      setDbState('done');
      toast.success(`Mylar pushed to DB — customer #${customerId}, 4 trains, proposal ${proposalNumber}`);
    } catch (e: any) {
      console.error(e);
      log.push(`✗ Error: ${e?.message ?? e}`);
      setDbLog([...log]);
      setDbState('error');
      toast.error(`DB push failed: ${e?.message ?? 'check console'}`);
    }
  };

  return (
    <div className="space-y-5 px-3 py-4">
      <PageHeader
        title="David Assessment — Mylar Specialty Films, Drungans"
        subtitle="Borehole pre-treatment replacement before RO. Simulated outcomes for 4 equipment trains (incl. DF as-quoted)."
        icon={Factory}
        actions={
          <div className="flex gap-2">
            <button
              onClick={handleSeed}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"
            >
              <Database className="w-3.5 h-3.5" />{seeded ? '✓ Local seed' : 'Seed locally'}
            </button>
            <button
              onClick={handlePushToDb}
              disabled={dbState === 'pushing'}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-purple-500/10 text-purple-400 text-xs font-bold border border-purple-500/20 hover:bg-purple-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Database className="w-3.5 h-3.5" />
              {dbState === 'pushing' ? 'Pushing to DB…' :
               dbState === 'done'    ? '✓ Pushed to DB' :
               dbState === 'error'   ? 'Retry DB push' :
                                       'Push to Database'}
            </button>
            <button
              onClick={() => navigate('/proposals')}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
            >
              <FileText className="w-3.5 h-3.5" />Open Proposals
            </button>
          </div>
        }
      />

      <div className="mb-2 text-center">
        <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
          Reynolds &amp; Bauhm Smart Tool 2.0 — David Yates / Mylar Drungans Assessment — All Rights Reserved
        </p>
      </div>

      {/* ── Tab navigation ──────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 border-b border-slate-700/50 pb-2">
        {([
          { k: 'assessment',  label: 'Process Assessment', icon: FlaskConical, c: 'cyan'    },
          { k: 'automation',  label: 'Automation & Control', icon: Gauge,      c: 'emerald' },
          { k: 'pricing',     label: 'Budget Pricing',      icon: TrendingUp,  c: 'amber'   },
          { k: 'proposals',   label: 'Proposals (PDF)',     icon: FileSignature, c: 'violet' },
        ] as const).map(t => {
          const isActive = activeTab === t.k;
          const Icon = t.icon;
          const colourMap: Record<string, string> = {
            cyan:    'from-cyan-500/30 to-cyan-600/10 border-cyan-400 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.35)]',
            emerald: 'from-emerald-500/30 to-emerald-600/10 border-emerald-400 text-emerald-200 shadow-[0_0_18px_rgba(16,185,129,0.35)]',
            amber:   'from-amber-500/30 to-amber-600/10 border-amber-400 text-amber-200 shadow-[0_0_18px_rgba(245,158,11,0.35)]',
            violet:  'from-violet-500/30 to-violet-600/10 border-violet-400 text-violet-200 shadow-[0_0_18px_rgba(139,92,246,0.35)]',
          };
          const dotMap: Record<string, string> = {
            cyan: 'bg-cyan-400', emerald: 'bg-emerald-400', amber: 'bg-amber-400', violet: 'bg-violet-400',
          };
          return (
            <button
              key={t.k}
              onClick={() => setActiveTab(t.k)}
              className={`group flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border-2 transition-all ${
                isActive
                  ? `bg-gradient-to-r ${colourMap[t.c]} animate-[rb-tab-pulse_2.4s_ease-in-out_infinite]`
                  : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}
            >
              {isActive && <span className={`w-2 h-2 rounded-full ${dotMap[t.c]} animate-pulse`} />}
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'assessment' && (<>
      {dbLog.length > 0 && (
        <div className={`p-4 rb-card border-l-4 ${
          dbState === 'done' ? 'border-emerald-500/50' :
          dbState === 'error' ? 'border-rose-500/50' :
          'border-purple-500/50'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Database className="w-4 h-4 text-purple-400" />
              Database Push Log
              {dbState === 'pushing' && <span className="text-[10px] text-purple-400 animate-pulse">working…</span>}
              {dbState === 'done' && <span className="text-[10px] text-emerald-400">complete</span>}
              {dbState === 'error' && <span className="text-[10px] text-rose-400">error</span>}
            </h3>
            <button onClick={() => { setDbLog([]); setDbState('idle'); }} className="text-[10px] text-slate-500 hover:text-slate-300">clear</button>
          </div>
          <div className="space-y-0.5 font-mono text-[10px]">
            {dbLog.map((line, i) => (
              <div key={i} className={
                line.startsWith('✓') ? 'text-emerald-400' :
                line.startsWith('✗') ? 'text-rose-400' :
                line.startsWith('  ') ? 'text-slate-400 pl-2' :
                'text-slate-300'
              }>{line}</div>
            ))}
          </div>
          {dbState === 'done' && (
            <p className="text-[11px] text-emerald-300 mt-3">
              All records now in production DB. Open <button onClick={() => navigate('/customers')} className="underline">/customers</button> to see Mylar Specialty Films,
              or <button onClick={() => navigate('/proposals')} className="underline">/proposals</button> for the proposal.
            </p>
          )}
        </div>
      )}

      {/* Customer / Site / Sample summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="p-4 rb-card space-y-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Building2 className="w-4 h-4 text-sky-400" />Customer</h3>
          <div className="text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Company</span><span className="text-white font-bold">Mylar Specialty Films</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Contact</span><span className="text-white">David Yates</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Role</span><span className="text-slate-300">Contractor PM (Homer Burgess Ltd)</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="text-slate-300 text-[10px]">david.yates_contractor@mylar.com</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="text-slate-300">+44 1387 258028</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Industry</span><span className="text-slate-300">Polyester film mfg (Celanese group)</span></div>
          </div>
        </div>

        <div className="p-4 rb-card space-y-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-emerald-400" />Site</h3>
          <div className="text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Location</span><span className="text-white font-bold text-right">Drungans, Dumfries DG2 8LT</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Region</span><span className="text-slate-300">Scotland, D&amp;G</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Borehole pump</span><span className="text-slate-300">70 m³/h @ 9 barg</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Actual draw</span><span className="text-white font-bold">24 m³/h (design flow)</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Daily use</span><span className="text-slate-300">150–200 m³</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Inlet pressure</span><span className="text-amber-400">2 barg (tight)</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Hall footprint</span><span className="text-slate-300">9 × 4.5 × 4.5 m</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Programme</span><span className="text-rose-400">Before Dec 2026</span></div>
          </div>
        </div>

        <div className="p-4 rb-card space-y-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-purple-400" />Borehole Sample</h3>
          <p className="text-[10px] text-slate-500">Veolia HW251217.24 · 28 Dec 2025</p>
          <div className="text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Fe (dissolved)</span><span className="text-rose-400 font-bold">0.47 mg/L</span></div>
            <div className="flex justify-between"><span className="text-slate-500">RO design Fe</span><span className="text-emerald-400">≤0.01 mg/L</span></div>
            <div className="flex justify-between"><span className="text-slate-500">pH</span><span className="text-slate-300">7.8</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Alkalinity</span><span className="text-slate-300">84 mg/L CaCO₃</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Hardness</span><span className="text-slate-300">205 mg/L CaCO₃</span></div>
            <div className="flex justify-between"><span className="text-slate-500">TSS</span><span className="text-slate-300">&lt;1 mg/L</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Turbidity</span><span className="text-slate-300">0.35 NTU</span></div>
            <div className="flex justify-between"><span className="text-slate-500">PSD mean</span><span className="text-amber-400 font-bold">1.25 µm</span></div>
            <div className="flex justify-between"><span className="text-slate-500">% sub-4 µm</span><span className="text-rose-400 font-bold">100%</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Mn</span><span className="text-amber-400">not tested ⚠</span></div>
          </div>
        </div>
      </div>

      {/* Problem statement */}
      <div className="p-4 rb-card border-l-4 border-rose-500/50">
        <h3 className="text-sm font-bold text-rose-400 mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4" />Problem Statement (why this filter, why now)</h3>
        <div className="text-xs text-slate-300 space-y-2 leading-relaxed">
          <p>• Existing sand filter station is <strong>bypassed</strong> — borehole water goes straight to RO.</p>
          <p>• Borehole Fe (0.47 mg/L) is <strong>47× higher</strong> than the RO design basis (0.01 mg/L). This is the root cause of RO membrane fouling and shortened life.</p>
          <p>• 100% of measured particles are below 4 µm (Veolia PSD report). <strong>A plain quartz/multimedia filter cannot capture them</strong> — granular media without coagulation has a practical cut-off of ~10–20 µm.</p>
          <p>• Konrad Śliwiński (Dynamik Filtr) offered 2× DN1800 plain quartz at €70 k EXW — this configuration <strong>solves neither problem</strong> and was priced before the chemistry / PSD data were shared.</p>
        </div>
      </div>

      {/* Train picker — single-click selects the simulation; ⚙ Open in editor pre-loads the same chain in TrainEditPage */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {TRAIN_DEFS.map((def) => {
          const result = allTrainResults[def.key];
          const active = activeTrain === def.key;
          const meets = result.meetsRoTarget;
          // Pre-loaded equipment chain that TrainEditPage will hydrate. Keys must
          // match EQUIPMENT_LIBRARY in TrainEditPage. Params are sensible defaults.
          const presetStages: Array<{ key: string; params: Record<string, any> }> = (() => {
            switch (def.key) {
              case 'df_quoted':
                return [
                  { key: 'multimedia', params: { vesselDiameter: 1.8, bedDepth: 1.2, numVessels: 2, anthraciteDepth: 0.5, sandDepth: 0.4, garnetDepth: 0.3, anthraciteES: 1.2, sandES: 0.5, garnetES: 0.3, backwashRate: 30 } },
                ];
              case 'df_revised':
                return [
                  { key: 'aeration_column',  params: { columnDiameter: 1.6, columnHeight: 2.5, airToWaterRatio: 8, airPressure: 0.4, packingSize: 50, packingType: 'tellerette', freeboard: 0.5 } },
                  { key: 'greensand',        params: { media: 'pyrolusite', vesselDiameter: 1.6, bedDepth: 0.9, numVessels: 2, kmno4Dose: 0, backwashRate: 30, arrangement: 'duplex_parallel', freeboard: 0.5, serviceVelocity: 8 } },
                  { key: 'multimedia',       params: { vesselDiameter: 1.6, bedDepth: 1.0, numVessels: 2, anthraciteDepth: 0.5, sandDepth: 0.3, garnetDepth: 0.2, anthraciteES: 1.4, sandES: 0.55, garnetES: 0.3, backwashRate: 25 } },
                ];
              case 'budget':
                return [
                  { key: 'pacl_dosing',     params: { dose: 1.5, contactTime: 60, tankVolume: 200 } },
                  { key: 'multimedia',      params: { vesselDiameter: 1.4, bedDepth: 1.0, numVessels: 2, anthraciteDepth: 0.5, sandDepth: 0.3, garnetDepth: 0.2, anthraciteES: 1.4, sandES: 0.55, garnetES: 0.3, backwashRate: 25 } },
                  { key: 'cartridge_polish', params: { micron: 5, housings: 1, cartridgesPerHousing: 6, changeOutMonths: 2 } },
                ];
              case 'recommended':
                return [
                  { key: 'aeration_column',  params: { columnDiameter: 0.9, columnHeight: 2.5, airToWaterRatio: 8, airPressure: 0.4, packingSize: 50, packingType: 'tellerette', freeboard: 0.5 } },
                  { key: 'greensand',        params: { media: 'greensand_plus', vesselDiameter: 1.6, bedDepth: 0.9, numVessels: 2, kmno4Dose: 1.5, backwashRate: 30, arrangement: 'duplex_parallel', freeboard: 0.5, serviceVelocity: 8 } },
                  { key: 'cartridge_polish', params: { micron: 5, housings: 1, cartridgesPerHousing: 6, changeOutMonths: 2 } },
                ];
              case 'premium':
                return [
                  { key: 'aeration_column',  params: { columnDiameter: 0.9, columnHeight: 2.5, airToWaterRatio: 8, airPressure: 0.4, packingSize: 50, packingType: 'tellerette', freeboard: 0.5 } },
                  { key: 'greensand',        params: { media: 'greensand_plus', vesselDiameter: 1.4, bedDepth: 0.9, numVessels: 2, kmno4Dose: 1.5, backwashRate: 30, arrangement: 'duplex_parallel', freeboard: 0.5, serviceVelocity: 8 } },
                  { key: 'uf',               params: { fibre: 'pvdf', poreSize: 0.03, numModules: 4, moduleArea: 50, flux: 60, recovery: 92, fibreOD: 1.3, fibreID: 0.7, fibresPerModule: 9000, backwashInterval: 30, backwashDuration: 60, cebInterval: 12, cipInterval: 30 } },
                  { key: 'cartridge_polish', params: { micron: 5, housings: 1, cartridgesPerHousing: 6, changeOutMonths: 2 } },
                ];
            }
          })();
          const savedId = savedTrainIds[def.key];
          const openInEditor = (e: React.MouseEvent) => {
            e.stopPropagation();
            try {
              localStorage.setItem('rb_train_preset_pending', JSON.stringify({
                name: `Mylar Drungans — ${def.name}`,
                description: def.description,
                influent: MYLAR_SAMPLE_PARAMS,
                customerCompany: MYLAR_CUSTOMER.company,
                stages: presetStages,
              }));
              navigate('/train-edit');
            } catch (err: any) {
              toast.error(`Could not preload editor: ${err?.message ?? err}`);
            }
          };
          const openSavedTrain = (e: React.MouseEvent) => {
            e.stopPropagation();
            navigate(`/train-edit?id=${savedId}`);
          };
          return (
            <div
              key={def.key}
              onClick={() => setActiveTrain(def.key)}
              className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                active
                  ? `bg-${def.accent}-500/10 border-${def.accent}-500/40`
                  : 'bg-rb-elevated border-slate-800 hover:border-slate-700'
              }`}
              style={active ? { borderColor: def.color + '66', backgroundColor: def.color + '0F' } : {}}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase" style={{ color: def.color }}>{def.name}</p>
                {meets ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertCircle className="w-4 h-4 text-amber-400" />}
              </div>
              <p className="text-[11px] text-slate-300 leading-tight mb-2">{def.subtitle}</p>
              <p className="text-[10px] text-slate-500 leading-tight mb-3">{def.description}</p>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <p className="text-slate-500">Capex</p>
                  <p className="font-bold" style={{ color: def.color }}>£{(result.capexEstimateGBP / 1000).toFixed(0)}k</p>
                </div>
                <div>
                  <p className="text-slate-500">Opex/yr</p>
                  <p className="font-bold" style={{ color: def.color }}>£{(result.opexAnnualGBP / 1000).toFixed(1)}k</p>
                </div>
                <div>
                  <p className="text-slate-500">Final Fe</p>
                  <p className={result.finalFe <= 0.05 ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>{result.finalFe.toFixed(3)} mg/L</p>
                </div>
                <div>
                  <p className="text-slate-500">Final SDI</p>
                  <p className={result.finalSdi <= 3 ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>{result.finalSdi.toFixed(1)}</p>
                </div>
              </div>
              <div className={`mt-3 text-[10px] font-bold uppercase ${meets ? 'text-emerald-400' : 'text-amber-400'}`}>
                {meets ? '✓ Meets RO design basis' : '⚠ Below RO design basis'}
              </div>
              {/* If this train has been saved to the DB (Push to Database), deep-link
                  straight to the actual generated train so it opens for editing/rename. */}
              {savedId ? (
                <button
                  onClick={openSavedTrain}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border-2 transition-all hover:brightness-125"
                  style={{
                    background: `linear-gradient(135deg, ${def.color}40, ${def.color}15)`,
                    borderColor: def.color,
                    color: def.color,
                    boxShadow: `0 0 14px ${def.color}66`,
                  }}
                  title={`Open the saved process train (id=${savedId}) — edit, rename and re-simulate`}
                >
                  🔗 Open saved train #{savedId} →
                </button>
              ) : null}
              {/* Open in Process Train editor with this chain preloaded (fresh preset) */}
              <button
                onClick={openInEditor}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border-2 transition-all hover:brightness-125"
                style={savedId ? {
                  background: 'transparent',
                  borderColor: def.color + '55',
                  color: def.color,
                } : {
                  background: `linear-gradient(135deg, ${def.color}30, ${def.color}10)`,
                  borderColor: def.color,
                  color: def.color,
                  boxShadow: `0 0 14px ${def.color}55`,
                }}
                title="Load this chain into the Process Train Editor as a fresh, unsaved preset"
              >
                ⚙ {savedId ? 'Open as new preset' : 'Open in Process Train Editor'} →
              </button>
            </div>
          );
        })}
      </div>

      {/* Active train: stage-by-stage simulation */}
      <div className="p-5 rb-card space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            Simulated Outcome — {TRAIN_DEFS.find(t => t.key === activeTrain)!.name}
          </h3>
          <div className="flex gap-2">
            {TRAIN_DEFS.find(t => t.key === activeTrain)!.equipmentRoutes.map((eq, i) => {
              const Icon = eq.icon;
              return (
                <button
                  key={i}
                  onClick={() => navigate(eq.route)}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-rb-elevated border border-slate-800 hover:border-slate-700 text-[10px] text-slate-300 transition-all"
                >
                  <Icon className="w-3 h-3" />{eq.name}
                </button>
              );
            })}
            <button
              onClick={() => navigate('/train-edit')}
              className="flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-[10px] text-amber-300 font-bold hover:bg-amber-500/20 transition-all"
              title="Open in live train editor — adjust influent, equipment, see real-time RO compliance"
            >
              ⚡ Edit in live editor
            </button>
          </div>
        </div>

        {/* Stage table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
                <th className="text-left py-2 px-2">Stage</th>
                <th className="text-right py-2 px-2">Fe in → out</th>
                <th className="text-right py-2 px-2">Mn in → out</th>
                <th className="text-right py-2 px-2">SDI in → out</th>
                <th className="text-right py-2 px-2">Turbidity</th>
                <th className="text-center py-2 px-2">RO-ready?</th>
              </tr>
            </thead>
            <tbody>
              {trainResult.stages.map((s, i) => (
                <tr key={i} className="border-b border-slate-900 hover:bg-rb-elevated/30">
                  <td className="py-3 px-2">
                    <p className="text-white font-medium">{s.name}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">{s.notes}</p>
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-slate-500">{s.feIn.toFixed(3)}</span>
                    <ArrowRight className="inline w-3 h-3 mx-1 text-slate-600" />
                    <span className={s.feOut <= 0.05 ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>{s.feOut.toFixed(3)}</span>
                  </td>
                  <td className="py-3 px-2 text-right text-slate-300">
                    {s.mnIn.toFixed(3)} → {s.mnOut.toFixed(3)}
                  </td>
                  <td className="py-3 px-2 text-right">
                    <span className="text-slate-500">{s.sdiIn.toFixed(1)}</span>
                    <ArrowRight className="inline w-3 h-3 mx-1 text-slate-600" />
                    <span className={s.sdiOut <= 3 ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>{s.sdiOut.toFixed(1)}</span>
                  </td>
                  <td className="py-3 px-2 text-right text-slate-300">
                    {s.turbIn.toFixed(2)} → {s.turbOut.toFixed(2)} NTU
                  </td>
                  <td className="py-3 px-2 text-center">
                    {s.meetsRoTarget ? <CheckCircle2 className="inline w-4 h-4 text-emerald-400" /> : <AlertCircle className="inline w-4 h-4 text-slate-600" />}
                  </td>
                </tr>
              ))}
              <tr className="bg-rb-elevated/40 font-bold">
                <td className="py-3 px-2 text-white">FINAL → RO Membranes</td>
                <td className="py-3 px-2 text-right">
                  <span className={trainResult.finalFe <= 0.05 ? 'text-emerald-400' : 'text-rose-400'}>{trainResult.finalFe.toFixed(3)} mg/L</span>
                  <span className="text-[10px] text-slate-500 ml-1">(target ≤0.01)</span>
                </td>
                <td className="py-3 px-2 text-right text-slate-300">{trainResult.finalMn.toFixed(3)} mg/L</td>
                <td className="py-3 px-2 text-right">
                  <span className={trainResult.finalSdi <= 3 ? 'text-emerald-400' : 'text-rose-400'}>{trainResult.finalSdi.toFixed(1)}</span>
                  <span className="text-[10px] text-slate-500 ml-1">(target ≤3)</span>
                </td>
                <td className="py-3 px-2 text-right text-emerald-400">{trainResult.finalTurb.toFixed(2)} NTU</td>
                <td className="py-3 px-2 text-center">
                  {trainResult.meetsRoTarget ? (
                    <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase">YES</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold uppercase">NO</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Notes */}
        <div className="space-y-2 pt-2 border-t border-slate-800">
          {trainResult.notes.map((n, i) => (
            <p key={i} className="text-xs text-slate-300 leading-relaxed">• {n}</p>
          ))}
        </div>

        {/* Commercial summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-800">
          <div className="p-3 rb-inner-card">
            <p className="text-[10px] text-slate-500 uppercase">Capex (R&amp;B → Mylar)</p>
            <p className="text-lg font-bold text-white">£{(trainResult.capexEstimateGBP / 1000).toFixed(0)}k</p>
          </div>
          <div className="p-3 rb-inner-card">
            <p className="text-[10px] text-slate-500 uppercase">Opex (consumables/yr)</p>
            <p className="text-lg font-bold text-white">£{(trainResult.opexAnnualGBP / 1000).toFixed(1)}k</p>
          </div>
          <div className="p-3 rb-inner-card">
            <p className="text-[10px] text-slate-500 uppercase">RO compliance</p>
            <p className={`text-lg font-bold ${trainResult.meetsRoTarget ? 'text-emerald-400' : 'text-amber-400'}`}>
              {trainResult.meetsRoTarget ? 'PASS' : 'PARTIAL'}
            </p>
          </div>
          <div className="p-3 rb-inner-card">
            <p className="text-[10px] text-slate-500 uppercase">10-yr total</p>
            <p className="text-lg font-bold text-white">£{((trainResult.capexEstimateGBP + trainResult.opexAnnualGBP * 10) / 1000).toFixed(0)}k</p>
          </div>
        </div>
      </div>

      {/* Comparison panel */}
      <div className="p-5 rb-card">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Gauge className="w-4 h-4 text-cyan-400" />Side-by-side comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-800">
                <th className="text-left py-2 px-2">Metric</th>
                {TRAIN_DEFS.map(t => (
                  <th key={t.key} className="text-right py-2 px-2" style={{ color: t.color }}>{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Final Fe (target ≤0.01 mg/L)', get: (r: TrainSimResult) => r.finalFe.toFixed(3) + ' mg/L', color: (r: TrainSimResult) => r.finalFe <= 0.05 ? 'text-emerald-400' : 'text-rose-400' },
                { label: 'Final SDI (target ≤3)', get: (r: TrainSimResult) => r.finalSdi.toFixed(1), color: (r: TrainSimResult) => r.finalSdi <= 3 ? 'text-emerald-400' : 'text-rose-400' },
                { label: 'Final turbidity', get: (r: TrainSimResult) => r.finalTurb.toFixed(2) + ' NTU', color: () => 'text-emerald-400' },
                { label: 'Capex', get: (r: TrainSimResult) => '£' + (r.capexEstimateGBP / 1000).toFixed(0) + 'k', color: () => 'text-white font-bold' },
                { label: 'Opex / year', get: (r: TrainSimResult) => '£' + (r.opexAnnualGBP / 1000).toFixed(1) + 'k', color: () => 'text-white' },
                { label: '10-year total', get: (r: TrainSimResult) => '£' + ((r.capexEstimateGBP + r.opexAnnualGBP * 10) / 1000).toFixed(0) + 'k', color: () => 'text-white font-bold' },
                { label: 'Meets RO design basis', get: (r: TrainSimResult) => r.meetsRoTarget ? 'YES' : 'NO', color: (r: TrainSimResult) => r.meetsRoTarget ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold' },
                { label: 'Stages in train', get: (r: TrainSimResult) => String(r.stages.length), color: () => 'text-slate-300' },
              ].map((row, i) => (
                <tr key={i} className="border-b border-slate-900">
                  <td className="py-2 px-2 text-slate-400">{row.label}</td>
                  {(['df_quoted', 'df_revised', 'budget', 'recommended', 'premium'] as const).map(k => {
                    const r = allTrainResults[k];
                    return (
                      <td key={k} className={`py-2 px-2 text-right ${row.color(r)}`}>{row.get(r)}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-400 leading-relaxed">
          <strong className="text-emerald-400">Recommendation:</strong> Option B (Aeration + Greensand Plus duplex + cartridge polish) is the lowest-cost configuration that
          fully meets the RO design basis. Option A (budget) leaves residual iron in the RO feed. Option C (premium) adds UF for guaranteed SDI under all operating
          scenarios — worth the £73 k uplift if Mylar plans to expand capacity or wants 15+ year asset life with minimal operator attention.
        </p>
      </div>
      </>)}

      {activeTab === 'automation' && <AutomationTab />}
      {activeTab === 'pricing' && <PricingTab />}
      {activeTab === 'proposals' && (
        <MylarProposalsTab results={{
          budget: allTrainResults.budget,
          recommended: allTrainResults.recommended,
          premium: allTrainResults.premium,
        }} />
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500 whitespace-nowrap">{k}</span>
      <span className="text-slate-200 text-right">{v}</span>
    </div>
  );
}

/* ─── Automation & Control Tab ──────────────────────────── */
function AutomationTab() {
  return (
    <div className="space-y-5">
      <div className="p-4 rb-card border-l-4 border-emerald-500/60">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Gauge className="w-5 h-5 text-emerald-400" />
          Automation, Instrumentation &amp; Control — Mylar Drungans Pre-treatment
        </h2>
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          Control philosophy and electrical scope for the Reynolds &amp; Bauhm recommended train. Designed for unmanned 24/7 operation
          with remote SCADA visibility and a single-point alarm to the Mylar boilerhouse DCS. This page is for engineering reference;
          commercial pricing is on the Budget Pricing tab.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="p-4 rb-card space-y-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Factory className="w-4 h-4 text-cyan-400" />PLC &amp; HMI platform</h3>
          <div className="text-xs space-y-1.5">
            <Row k="PLC" v="Siemens SIMATIC S7-1500 (CPU 1515-2 PN)" />
            <Row k="Programming" v="TIA Portal v18, structured text + LAD blocks" />
            <Row k="Redundancy" v="Hot-standby CPU pair (S7-1500H) on Option C / Premium" />
            <Row k="HMI" v="SIMATIC HMI Comfort Panel 12 inch (TP1200)" />
            <Row k="Visualisation" v="WinCC Unified — process mnemonics, trend pens, alarm pages" />
            <Row k="Historian" v="Local SD card 30-day ring buffer + optional WinCC OA push" />
            <Row k="Time sync" v="NTP from site domain controller" />
          </div>
        </div>

        <div className="p-4 rb-card space-y-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Layers className="w-4 h-4 text-amber-400" />I/O architecture</h3>
          <div className="text-xs space-y-1.5">
            <Row k="Distributed I/O" v="ET 200SP IM 155-6 PN HF, 1 head + 32 modules" />
            <Row k="Digital inputs" v="48 channels — limit switches, level, valve positions" />
            <Row k="Digital outputs" v="32 channels (relay) — valve solenoids, lamp tower, pumps" />
            <Row k="Analogue inputs" v="16 channels (4–20 mA HART) — flow, pH, ORP, turbidity, dP" />
            <Row k="Analogue outputs" v="8 channels (4–20 mA) — VFD references, dosing pump speed" />
            <Row k="Safety" v="F-CPU + ET 200SP F-DI/F-DQ for emergency stop loops (SIL 2)" />
            <Row k="Network" v="PROFINET RT in a managed ring topology (Scalance XB-200)" />
          </div>
        </div>

        <div className="p-4 rb-card space-y-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Wind className="w-4 h-4 text-sky-400" />Drives &amp; power (MCC)</h3>
          <div className="text-xs space-y-1.5">
            <Row k="MCC enclosure" v="Form 3b, 2000 x 800 x 600 mm, IP54, Rittal TS8" />
            <Row k="Incomer" v="63 A TP+N MCCB, surge protection, energy meter" />
            <Row k="Backwash pump VFD" v="SINAMICS G120C, 7.5 kW, PROFINET, BACnet bridge" />
            <Row k="Air blower VFD" v="SINAMICS G120C, 5.5 kW, EMC C2 filter" />
            <Row k="Dosing pumps" v="Grundfos DDA AR/FCM, 4-20 mA + status feedback" />
            <Row k="UPS" v="SITOP UPS1600 (24 V DC, 20 Ah) — instrument loop ride-through" />
            <Row k="Lighting / heater" v="Local lighting + cabinet anti-condensation heater" />
          </div>
        </div>

        <div className="p-4 rb-card space-y-3">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><Beaker className="w-4 h-4 text-rose-400" />Instrumentation (field)</h3>
          <div className="text-xs space-y-1.5">
            <Row k="Raw water flow" v="Endress+Hauser Promag W400 DN100 (mag flow)" />
            <Row k="Treated water flow" v="Promag W400 DN100 + totaliser" />
            <Row k="pH / ORP" v="Memosens CPS11D + CPM253 transmitter, weekly cal" />
            <Row k="Turbidity" v="E+H Turbimax CUS52D — RO feed line, 4-20 mA" />
            <Row k="Iron (online)" v="Hach EZ1000 colourimetric analyser, 0–2 mg/L" />
            <Row k="dP across filter" v="Rosemount 3051 wet/wet, 0–2 bar" />
            <Row k="Level (RO break tank)" v="VEGAPULS C 21 radar, 0–4 m" />
            <Row k="Backwash flow" v="Insertion paddle, 4-20 mA" />
          </div>
        </div>

        <div className="p-4 rb-card space-y-3 lg:col-span-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><CircleDot className="w-4 h-4 text-emerald-400" />Control philosophy &amp; sequencing</h3>
          <ul className="text-xs text-slate-300 space-y-1.5 list-disc list-inside leading-relaxed">
            <li><strong className="text-white">Auto / Manual / Maintenance modes</strong> per equipment item, key-switched on the HMI.</li>
            <li><strong className="text-white">Backwash trigger</strong> by whichever comes first: dP &gt; 0.7 bar, throughput &gt; 500 m³, or 24-hour timer.
                Auto-staggered between duty vessels with anti-coincidence interlock.</li>
            <li><strong className="text-white">KMnO4 dosing</strong> proportional to raw-water flow with feed-forward trim on online Fe reading.
                Slow shutdown if ORP at filter outlet drops below setpoint.</li>
            <li><strong className="text-white">Aeration column</strong> blower runs whenever there is flow; air-water ratio held at design setpoint via VFD trim.</li>
            <li><strong className="text-white">UV reactor</strong> (Option C) interlocked with low-flow trip, low-UVT trip, and lamp end-of-life alarm.</li>
            <li><strong className="text-white">Safety interlocks (F-PLC)</strong>: emergency stop ring, hydrogen LEL trip (electrochlorination link), high-high level on RO break tank, low-low suction pressure.</li>
            <li><strong className="text-white">Alarm hierarchy</strong>: Critical (audible + DCS digital out), High (HMI banner), Warning (logged only).</li>
            <li><strong className="text-white">Reporting</strong>: daily KPI email (Fe, SDI surrogate, dP trends, backwash counts, chemical usage) generated by the historian.</li>
          </ul>
        </div>

        <div className="p-4 rb-card space-y-3 lg:col-span-2">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><ArrowRight className="w-4 h-4 text-purple-400" />Network &amp; remote-access architecture</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rb-inner-card">
              <div className="text-emerald-400 font-bold mb-1">Field layer</div>
              <p className="text-slate-300 leading-relaxed">PROFINET ring with media-redundancy protocol (MRP). All field devices on managed Scalance XB-200 switches; ring recovery &lt; 200 ms.</p>
            </div>
            <div className="p-3 rb-inner-card">
              <div className="text-cyan-400 font-bold mb-1">Control layer</div>
              <p className="text-slate-300 leading-relaxed">S7-1500 CPU on the PROFINET ring + a separate management Ethernet port for engineering and SCADA. Strict L2 segregation.</p>
            </div>
            <div className="p-3 rb-inner-card">
              <div className="text-amber-400 font-bold mb-1">Plant / DCS layer</div>
              <p className="text-slate-300 leading-relaxed">OPC UA server in the PLC exposes a hand-picked variable list to the Mylar DCS. Dry contact for top-level OK / Alarm.</p>
            </div>
            <div className="p-3 rb-inner-card">
              <div className="text-rose-400 font-bold mb-1">Remote access</div>
              <p className="text-slate-300 leading-relaxed">Secomea SiteManager (or Siemens SCALANCE M-800) cellular VPN; outbound-only, Mylar-controlled access window, audit trail.</p>
            </div>
            <div className="p-3 rb-inner-card">
              <div className="text-purple-400 font-bold mb-1">Cyber posture</div>
              <p className="text-slate-300 leading-relaxed">IEC 62443-3-3 SL-2 baseline: signed firmware, role-based HMI logins, no default credentials, weekly log export.</p>
            </div>
            <div className="p-3 rb-inner-card">
              <div className="text-sky-400 font-bold mb-1">Documentation</div>
              <p className="text-slate-300 leading-relaxed">Full PFD, P&amp;ID, control narrative, I/O list, loop diagrams, FAT/SAT protocols and HAZOP report issued as part of detailed design.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Budget Pricing Tab ────────────────────────────────── */
function PricingTab() {
  const options = [
    {
      key: 'A', label: 'Option A — Budget', colour: 'amber',
      capex: 88000, opex: 14500,
      mechanical: 56000, automation: 18000, install: 10000, commission: 4000,
      desc: 'Pressurised aeration + single Greensand Plus vessel + cartridge polish. Lowest entry price; meets the RO basis under steady-state but leaves no redundancy.',
      pros: [
        'Lowest capital outlay — closes the December 2026 replacement window.',
        'All equipment off-the-shelf, short lead-time.',
      ],
      cons: [
        'Single catalytic vessel — backwash is plant-stopping (no duty/standby).',
        'No UF — SDI margin to RO is tight during winter (low temperature, low DO).',
        'No online iron analyser — operator relies on lab spot-checks.',
        'No spare media stock — replenishment cost in year 5–6 not bundled.',
      ],
    },
    {
      key: 'B', label: 'Option B — Recommended', colour: 'emerald',
      capex: 142000, opex: 11800,
      mechanical: 92000, automation: 28000, install: 16000, commission: 6000,
      desc: 'Pressurised aeration column + duplex Greensand Plus (duty/standby) + cartridge polish + online Fe analyser. Fully meets the RO basis with margin.',
      pros: [
        'Duty/standby duplex — backwash is non-disruptive to the RO feed.',
        'Online Fe analyser feeds the KMnO4 dosing loop in closed control.',
        '304L vessels (not painted CS) — true 15+ year service life.',
        'Bundled Siemens S7-1500 PLC + WinCC HMI + Mylar DCS handshake.',
        'Includes UK installation, commissioning and FAT/SAT.',
        'Performance guaranteed against RO feed-water envelope at SAT.',
      ],
      cons: [
        'CAPEX ~£54 k higher than budget option.',
        'Annual chemical OPEX dominated by KMnO4 — visible line in P&L.',
      ],
    },
    {
      key: 'C', label: 'Option C — Premium', colour: 'cyan',
      capex: 215000, opex: 13500,
      mechanical: 138000, automation: 44000, install: 22000, commission: 11000,
      desc: 'Option B + downstream UF (PVDF hollow fibre) + UV disinfection + hot-standby S7-1500H PLC + remote engineering VPN. Best-in-class asset for 15-year horizon.',
      pros: [
        'UF guarantees SDI15 < 3 to the RO under all influent conditions.',
        'UV provides a biological barrier — critical if any RO downtime allows biofilm growth.',
        'Hot-standby PLC — zero unplanned downtime from controller faults.',
        'OPC UA + cellular VPN — R&B engineers can diagnose remotely under SLA.',
        'Future-proof for capacity expansion or process water reuse.',
      ],
      cons: [
        'Highest capital — £73 k uplift versus Option B.',
        'More instrumentation = more calibration work for the operator (quarterly).',
      ],
    },
  ];
  return (
    <div className="space-y-5">
      <div className="p-4 rb-card border-l-4 border-amber-500/60">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-amber-400" />
          Budget pricing — three professional options
        </h2>
        <p className="text-xs text-slate-400 mt-2 leading-relaxed">
          Indicative installed-cost budget for the Reynolds &amp; Bauhm trains. All figures GBP, ex-VAT, valid 60 days from issue.
          Includes mechanical equipment, electrical, control panel, UK installation, commissioning and performance test. Excludes civils,
          power feed beyond the MCC, and any RO upgrade work.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {options.map(o => {
          const ringMap: Record<string, string> = {
            amber:   'border-amber-500/40 from-amber-500/10 to-transparent',
            emerald: 'border-emerald-500/60 from-emerald-500/15 to-transparent shadow-[0_0_30px_rgba(16,185,129,0.25)]',
            cyan:    'border-cyan-500/40 from-cyan-500/10 to-transparent',
          };
          const accentMap: Record<string, string> = { amber: 'text-amber-400', emerald: 'text-emerald-400', cyan: 'text-cyan-400' };
          return (
            <div key={o.key} className={`p-4 rb-card border-2 bg-gradient-to-b ${ringMap[o.colour]}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h3 className={`text-lg font-bold ${accentMap[o.colour]}`}>{o.label}</h3>
                {o.key === 'B' && <span className="text-[9px] font-bold uppercase tracking-widest bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-400/40">Recommended</span>}
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed mb-3">{o.desc}</p>

              <div className="rb-inner-card p-3 space-y-1 mb-3">
                <div className="flex justify-between text-xs"><span className="text-slate-500">Mechanical / process</span><span className="text-white font-mono">£{o.mechanical.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Automation / electrical</span><span className="text-white font-mono">£{o.automation.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">UK installation</span><span className="text-white font-mono">£{o.install.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs"><span className="text-slate-500">Commissioning / FAT/SAT</span><span className="text-white font-mono">£{o.commission.toLocaleString()}</span></div>
                <div className="border-t border-slate-700 my-1.5"></div>
                <div className="flex justify-between text-sm font-bold"><span className="text-slate-300">CAPEX total</span><span className={`font-mono ${accentMap[o.colour]}`}>£{o.capex.toLocaleString()}</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">Annual OPEX (yr 1)</span><span className="text-slate-200 font-mono">£{o.opex.toLocaleString()}/yr</span></div>
                <div className="flex justify-between text-[11px]"><span className="text-slate-500">10-yr life-cycle</span><span className="text-slate-200 font-mono">£{(o.capex + o.opex * 10).toLocaleString()}</span></div>
              </div>

              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">Advantages</div>
                <ul className="text-[11px] text-slate-300 leading-relaxed space-y-0.5 list-disc list-inside">
                  {o.pros.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold mb-1">Disadvantages</div>
                <ul className="text-[11px] text-slate-300 leading-relaxed space-y-0.5 list-disc list-inside">
                  {o.cons.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-4 rb-card border-l-4 border-cyan-500/60">
        <h3 className="text-sm font-bold text-white mb-1">Like-for-like comparison vs Dynamik Filtr — revised proposal (28 May 2026)</h3>
        <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
          Konrad's revised scope (aeration + Multisand MnO₂ + 2-stage filtration, no KMnO₄, optional auxiliaries, Scotland install crew) is a major
          improvement on the original €70,000 EXW quartz-only quote. A firm price for the revised scope has not yet been issued —
          Konrad asked for agreement on the concept before calculating values, so the DF figures below are provisional.
        </p>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-700 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="text-left py-2">Scope item</th>
              <th className="text-right py-2">DF revised (Konrad)</th>
              <th className="text-right py-2">R&amp;B Option B</th>
              <th className="text-right py-2">R&amp;B Option C</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Upstream aeration',              '✓ DN1600 pressurised', '✓ pressurised column', '✓ pressurised column'],
              ['Catalytic Fe/Mn media',          '✓ Multisand MnO₂ ×2', '✓ Greensand Plus duplex', '✓ Greensand Plus duplex'],
              ['KMnO₄ dosing required',          '✓ none (natural ore)', '✗ KMnO₄ consumable', '✗ KMnO₄ consumable'],
              ['2nd-stage polishing',            '⚠ media unspecified', '✓ cartridge polish', '✓ UF (PVDF)'],
              ['SDI<3 to RO guaranteed',         '✗ not guaranteed', '~ cartridge (good)', '✓ UF absolute'],
              ['UV disinfection',                '✗', '✗', '✓'],
              ['Vessel material',               '? not stated', '✓ 304L', '✓ 304L'],
              ['Inlet pressure',               '⚠ needs 4 bar (site ~2)', '✓ designed for site', '✓ designed for site'],
              ['Backwash pump + blower',         '✓ can include', '✓', '✓'],
              ['Backwash water source',          '↺ reuse 200 m³ tank', 'new break tank', 'new break tank'],
              ['Online Fe analyser',             '✗', '✓', '✓'],
              ['PLC + HMI',                      '✓ Siemens', '✓ Siemens', '✓ Siemens hot-standby'],
              ['UK installation',                '✓ DF Scotland crew', '✓', '✓'],
              ['Performance guarantee',          '⚠ subject to revision', '✓', '✓'],
              ['Price status',                   'not yet issued', '£142,000 (fixed)', '£215,000 (fixed)'],
            ].map((row, i) => (
              <tr key={i} className="border-b border-slate-800/50">
                {row.map((cell, j) => (
                  <td key={j} className={`py-1.5 ${j === 0 ? 'text-slate-400' : 'text-right font-mono ' + (j === 1 ? 'text-amber-300' : j === 2 ? 'text-emerald-300' : 'text-cyan-300')}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
          <strong className="text-amber-300">Open items before accepting a DF price:</strong> (1) the 4 bar inlet request vs ~2 barg available on site —
          a booster-pump decision; (2) vessel material of construction (painted CS vs 304L); (3) polishing-stage media and a <em>guaranteed</em> RO-feed
          SDI₁₅; (4) free-chlorine removal ahead of the RO if shock chlorination is retained; and (5) a firm price. The natural-MnO₂ media (no KMnO₄) is a
          genuine OPEX advantage for DF, but Options B/C still uniquely guarantee SDI to the RO and carry a written performance guarantee.
        </p>
      </div>
    </div>
  );
}
